// Central catalog of module keys used by the sidebar and by the
// Users → Manage modules dialog. Adding a new module in Layout.jsx?
// Add a matching entry here so administrators can grant/deny access.
//
// A user with `module_access = null` (or missing) has FULL access.
// A user with `module_access = []` sees only Dashboard (always visible).
// A user with `module_access = ["issues", "assets"]` sees ONLY those.
//
// The Executive Dashboard (`dashboard`) is always visible — it is the
// landing page after login and cannot be turned off.

export const ALWAYS_ALLOWED = ["dashboard"];

// Roles that always have full access regardless of module_access. This mirrors
// the backend `_MODULE_BYPASS_ROLES` set — keep them in sync.
export const BYPASS_ROLES = ["managing_director", "executive_director"];

export const MODULE_GROUPS = [
  {
    label: "Overview",
    modules: [
      { key: "dashboard", label: "Executive Dashboard" },
      { key: "executive-rollup", label: "Executive Rollup" },
      { key: "reports",   label: "Reports & Analytics" },
    ],
  },
  {
    label: "Operations",
    modules: [
      { key: "issues",         label: "Issue Reporting" },
      { key: "inventory",      label: "Stock Incidents" },
      { key: "stock-requests", label: "Stock Requests" },
      { key: "assets",         label: "Assets" },
      { key: "vehicles",       label: "Vehicles" },
      { key: "fuel-logs",      label: "Fuel Logs" },
      { key: "accidents",      label: "Accidents" },
      { key: "maintenance",    label: "Maintenance" },
      { key: "luggage",        label: "Luggage Area" },
    ],
  },
  {
    label: "Website & Sales",
    modules: [
      { key: "products",       label: "Products" },
      { key: "orders",         label: "Online Orders" },
      { key: "news-admin",     label: "News & Updates" },
      { key: "careers-admin",  label: "Careers & Applications" },
      { key: "hero-slides",    label: "Homepage Banners" },
    ],
  },
  {
    label: "Stakeholders",
    modules: [
      { key: "suppliers",       label: "Suppliers" },
      { key: "contracts",       label: "Contracts" },
      { key: "deliveries",      label: "Deliveries" },
      { key: "purchases",       label: "Purchases" },
      { key: "feedback",        label: "Customer Feedback" },
      { key: "surveys",         label: "Surveys" },
      { key: "loyalty",         label: "Loyalty Members" },
      { key: "business-cards",  label: "Business Cards" },
    ],
  },
  {
    label: "Communications",
    modules: [
      { key: "messages",       label: "Internal Messages" },
      { key: "announcements",  label: "Announcements" },
      { key: "policy",         label: "Operations Policy" },
      { key: "documents",      label: "Documents" },
    ],
  },
  {
    label: "Administration",
    modules: [
      { key: "branches",        label: "Branches" },
      { key: "users",           label: "Users & Roles" },
      { key: "staff",           label: "Staff Bio Data" },
      { key: "attendance",      label: "Staff Attendance" },
      { key: "leave",           label: "Leave Management" },
      { key: "admin-requests",  label: "Admin Requests" },
      { key: "approvals",       label: "Approvals" },
      { key: "periodic-reports", label: "Periodic Reports" },
      { key: "audit-log",       label: "Audit Log" },
      { key: "backups",         label: "Backups" },
      { key: "tenants",         label: "Tenants (SaaS)" },
    ],
  },
];

// Flat list of every real module key (excluding always-allowed dashboard).
export const ALL_MODULE_KEYS = MODULE_GROUPS.flatMap((g) => g.modules.map((m) => m.key));

/**
 * Returns true if `user` may view module `key`.
 * - Managing Directors / Executive Directors bypass all restrictions.
 * - Users with no `module_access` field (null / undefined) have full access.
 * - Empty array = restricted (nothing visible outside ALWAYS_ALLOWED).
 * - Otherwise the key must be present in the array.
 */
export function canViewModule(user, key) {
  if (!user) return false;
  if (BYPASS_ROLES.includes(user.role)) return true;
  if (ALWAYS_ALLOWED.includes(key)) return true;
  const list = user.module_access;
  if (list === null || list === undefined) return true;
  return Array.isArray(list) && list.includes(key);
}
